import React, { useMemo } from "react";
import { Grid, Box, Select, Tooltip, MenuItem } from "@mui/material";
import { DeviceAvailableMetrics } from "../../Utils/AvailableMetrics";
import DmpIcons from "../../../../components/DmpIcons/icons";
import { DecreaseIcon, IncreaseIcon } from "../../../../assets/images/Icons";

export const arrowIcon = (isNegative) => {
    return isNegative ? <DecreaseIcon sx={{ color: "red" }} /> : <IncreaseIcon sx={{ color: "green" }} />
};

const formatMetricValue = (value) => {
    if (value === null || value === undefined || value === "") return "0";
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    
    if (numValue % 1 === 0) {
        return numValue.toString();
    }
    
    return numValue.toFixed(2).replace(/\.?0+$/, '');
};

const DEVICE_ICONS = {
    Desktop: { src: DmpIcons.DeviceIcons.DesktopIcon, alt: "Desktop logo", className: "iconDevice desktop" },
    Mobile: { src: DmpIcons.DeviceIcons.SmartphoneIcon, alt: "Smartphone logo", className: "iconDevice" },
    Tablet: { src: DmpIcons.DeviceIcons.TabletIcon, alt: "TabletIcon logo", className: "iconDevice tablet" }
};

const SELECT_STYLES = {
    color: "black",
    fontSize: "12px",
    fontFamily: "Inter",
    padding: "0",
    border: "none",
    fontWeight: 600,
    height: "28px",
    minWidth: "auto",
    background: "transparent",
    "& .MuiSelect-icon": { color: "#BAB7B7" },
    "& .MuiSelect-select": { paddingBottom: '0', paddingTop: '0' },
    "& fieldset": { border: "none", padding: 0 }
};

const MENU_ITEM_STYLES = {
    fontSize: "12px",
    padding: "4px 8px",
    lineHeight: "1.2",
    fontFamily: "Inter"
};

const DeviceIcon = ({ deviceName }) => {
    const iconConfig = DEVICE_ICONS[deviceName];
    return iconConfig ? (
        <img 
            src={iconConfig.src}
            alt={iconConfig.alt}
            className={iconConfig.className}
        />
    ) : null;
};

const MetricCell = ({ value, compareData, currencySymbol, align = "center" }) => (
    <td align={align}>
        <span className="table-cell-with-comparative">
            {formatMetricValue(value)}
            {compareData?.compare_enabled && (
                <Tooltip title={`${currencySymbol || ''}${formatMetricValue(compareData.compare_value)}`}>
                    <span style={{
                        color: compareData.compare_percent < 0 ? "red" : "green",
                        width: "45px",
                        display: "flex",
                        justifyContent: "space-between"
                    }}>
                        {arrowIcon(compareData.compare_percent < 0)} {compareData.compare_percent}%
                    </span>
                </Tooltip>
            )}
        </span>
    </td>
);

const TableDevice = ({ tableMetrics, handleTableMetricChange, tableData, currencySymbol }) => {
    const metricsEntries = useMemo(() => Object.entries(DeviceAvailableMetrics), []);
    const tableMetricsKeys = useMemo(() => Object.keys(tableMetrics), [tableMetrics]);
    const tableMetricsValues = useMemo(() => Object.values(tableMetrics), [tableMetrics]);

    const processedTableData = useMemo(() => 
        tableData.map(row => ({
            ...row,
            metrics: [
                {
                    value: row.metric1,
                    compare_enabled: row.compare_enabled,
                    compare_value: row.metric1_compare,
                    compare_percent: row.metric1_compare_percent
                },
                {
                    value: row.metric2,
                    compare_enabled: row.compare_enabled,
                    compare_value: row.metric2_compare,
                    compare_percent: row.metric2_compare_percent
                },
                {
                    value: row.metric3,
                    compare_enabled: row.compare_enabled,
                    compare_value: row.metric3_compare,
                    compare_percent: row.metric3_compare_percent
                }
            ]
        })), [tableData]
    );

    return (
        <Grid item xs={8} sx={{ paddingLeft: '20px !important' }}>
            <Grid container>
                <Grid item xs={12}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "left",
                        alignItems: "center",
                        fontSize: "12px",
                        fontWeight: "700"
                    }}>
                        TOP PERFORMING DEVICE
                    </Box>
                </Grid>
                <Grid item xs={12} className="metrics-table">
                    <table>
                        <thead>
                            <tr>
                                <th className="color-th"> </th>
                                <th> </th>
                                {tableMetricsKeys.map((metricKey, index) => (
                                    <th key={index}>
                                        <Select
                                            value={tableMetrics[metricKey]}
                                            onChange={(event) => handleTableMetricChange(event, metricKey)}
                                            sx={SELECT_STYLES}
                                        >
                                            {metricsEntries.map(([metric, label]) => (
                                                <MenuItem
                                                    key={metric}
                                                    value={metric}
                                                    disabled={tableMetricsValues.includes(metric) && tableMetrics[metricKey] !== metric}
                                                    sx={MENU_ITEM_STYLES}
                                                >
                                                    {label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {processedTableData.length > 0 ? (
                                processedTableData.map((row, index) => (
                                    <tr key={index}>
                                        <td className="color-td">
                                            <span
                                                className="device-color"
                                                style={{ backgroundColor: row.color }}
                                            />
                                        </td>
                                        <td className="first-td">
                                            <DeviceIcon deviceName={row.name} />
                                            {row.name}
                                        </td>
                                        <MetricCell 
                                            value={row.metrics[0].value}
                                            compareData={row.metrics[0]}
                                            currencySymbol={currencySymbol}
                                        />
                                        <MetricCell 
                                            value={row.metrics[1].value}
                                            compareData={row.metrics[1]}
                                            currencySymbol={currencySymbol}
                                            align="right"
                                        />
                                        <MetricCell 
                                            value={row.metrics[2].value}
                                            compareData={row.metrics[2]}
                                            currencySymbol={currencySymbol}
                                            align="right"
                                        />
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td align="center" className="color-td"> </td>
                                    <td colSpan={tableData.length + 1} align="center" className="no-data">
                                        No data available
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default TableDevice;